import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { paymentLinksService } from '../../services/paymentLinksService';
import { itinerariesService } from '../../services/itinerariesService';
import SecurePaymentDetailsModal from '../Payments/SecurePaymentDetailsModal';

// Voyager Pro reskin (Figma PaymentLinksPage) - list+drawer pattern matching
// Leads/Groups/Quotes/Itineraries. paymentLinksService.js is already fully
// unified (no independent branching needed), so this component is shared by
// both agency (/agent/payment-links) and independent (/independent/payment-links)
// agents - see memory entity "TPCRM Voyager Pro CRM Reskin Project".
//
// Figma's PaymentLinksPage also supports linking a payment link to a QUOTE
// (not just an itinerary) and an EmailPreviewModal for previewing the
// payment-request email before sending - NEITHER exists in the real backend
// today (payment-links.js has no quote_id column/param). Not added here -
// flagging as a real gap between Figma and the app, not silently dropped.

const TYPE_CFG = {
  deposit: { label: 'Deposit', color: 'text-purple-700', bg: 'bg-purple-100' },
  final_payment: { label: 'Final Payment', color: 'text-blue-700', bg: 'bg-blue-100' },
  installment: { label: 'Installment', color: 'text-amber-700', bg: 'bg-amber-100' },
  custom: { label: 'Custom', color: 'text-slate-600', bg: 'bg-slate-100' },
};

const STATUS_CFG = {
  pending: { label: 'Pending', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  completed: { label: 'Completed', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  expired: { label: 'Expired', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  cancelled: { label: 'Cancelled', color: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400' },
};

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors";
const labelCls = "block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5";

const fmtMoney = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
const isExpired = (expiresAt) => new Date() > new Date(expiresAt);

const BLANK_FORM = {
  client_email: '', client_name: '', client_phone: '',
  amount: '', payment_type: 'custom', description: '', custom_message: '', agent_notes: '',
  itinerary_id: '', travel_end_date: '', expires_in_hours: 72, send_email: true,
};

// ─── Create / Edit modal ────────────────────────────────────────────────────

function LinkFormModal({ initial, prefill, itineraries, onSave, onClose, saving }) {
  const isEdit = !!initial;
  const isCompleted = initial?.status === 'completed';
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        client_email: initial.client_email, client_name: initial.client_name, client_phone: initial.client_phone || '',
        amount: initial.amount, payment_type: initial.payment_type, description: initial.description || '',
        custom_message: initial.custom_message || '', agent_notes: initial.agent_notes || '',
        itinerary_id: initial.itinerary_id || '', travel_end_date: initial.travel_end_date ? String(initial.travel_end_date).split('T')[0] : '', expires_in_hours: 72, send_email: false,
      };
    }
    if (prefill) {
      const itinerary = itineraries.find(item => String(item.id) === String(prefill.itinerary_id));
      return {
        ...BLANK_FORM,
        client_email: prefill.email || '',
        client_name: prefill.name || '',
        client_phone: prefill.phone || '',
        itinerary_id: prefill.itinerary_id || '',
        travel_end_date: itinerary?.travel_end_date ? String(itinerary.travel_end_date).split('T')[0] : '',
      };
    }
    return BLANK_FORM;
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setItinerary = itineraryId => {
    const itinerary = itineraries.find(item => String(item.id) === String(itineraryId));
    setForm(prev => ({
      ...prev,
      itinerary_id: itineraryId,
      travel_end_date: itinerary?.travel_end_date ? String(itinerary.travel_end_date).split('T')[0] : prev.travel_end_date,
    }));
  };
  const valid = form.client_name.trim() && form.client_email.trim() && parseFloat(form.amount) > 0;
  const selectedItinerary = itineraries.find(item => String(item.id) === String(form.itinerary_id));
  const itineraryHasDate = Boolean(selectedItinerary?.travel_end_date);

  useEffect(() => {
    if (!form.travel_end_date && selectedItinerary?.travel_end_date) {
      setForm(prev => ({ ...prev, travel_end_date: String(selectedItinerary.travel_end_date).split('T')[0] }));
    }
  }, [form.travel_end_date, selectedItinerary?.travel_end_date]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">{isCompleted ? 'Edit Internal Details' : isEdit ? 'Edit Payment Link' : 'Create Payment Link'}</h2>
            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{isCompleted ? (initial?.travel_end_date_editable ? 'This payment has been completed - notes and the travel end date can be updated.' : 'This payment has been completed - notes can be updated; the travel end date can no longer be changed here.') : isEdit ? 'Update the details below.' : 'Link will be emailed to the client on creation.'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <div>
            <label className={labelCls}>Client Name *</label>
            <input className={inputCls} value={form.client_name} onChange={e => set('client_name', e.target.value)} disabled={isCompleted} />
          </div>
          <div>
            <label className={labelCls}>Client Email *</label>
            <input type="email" className={inputCls} value={form.client_email} onChange={e => set('client_email', e.target.value)} disabled={isCompleted} />
          </div>
          <div>
            <label className={labelCls}>Client Phone</label>
            <input className={inputCls} value={form.client_phone} onChange={e => set('client_phone', e.target.value)} disabled={isCompleted} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Amount *</label>
              <input type="number" step="0.01" className={inputCls} value={form.amount} onChange={e => set('amount', e.target.value)} disabled={isCompleted} />
            </div>
            <div>
              <label className={labelCls}>Payment Type</label>
              <select className={inputCls} value={form.payment_type} onChange={e => set('payment_type', e.target.value)} disabled={isCompleted}>
                {Object.entries(TYPE_CFG).map(([v, cfg]) => <option key={v} value={v}>{cfg.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input className={inputCls} value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g., Trip deposit for Hawaii vacation" disabled={isCompleted} />
          </div>
          <div>
            <label className={labelCls}>Custom Message</label>
            <textarea rows={3} className={inputCls} value={form.custom_message} onChange={e => set('custom_message', e.target.value)} placeholder="Add a personalized message for the client…" disabled={isCompleted} />
          </div>
          <div>
            <label className={labelCls}>Agent Notes (Internal Only)</label>
            <textarea rows={2} className={inputCls} value={form.agent_notes} onChange={e => set('agent_notes', e.target.value)} placeholder="Internal notes - not visible to client…" />
            <p className="text-xs text-[var(--muted-foreground)] mt-1">These notes are only visible to you and other agents.</p>
          </div>
          <div>
            <label className={labelCls}>Travel End Date (Internal Only, Optional)</label>
            <input
              type="date"
              className={inputCls}
              value={form.travel_end_date}
              onChange={e => set('travel_end_date', e.target.value)}
              disabled={initial?.travel_end_date_editable === false || itineraryHasDate}
            />
            <p className="text-xs text-[var(--muted-foreground)] mt-1">This date is only visible to agents and may be left blank.</p>
          </div>
          {!isCompleted && (
            <>
              <div>
                <label className={labelCls}>Link to Itinerary (Optional)</label>
                <select className={inputCls} value={form.itinerary_id} onChange={e => setItinerary(e.target.value)}>
                  <option value="">None - Standalone Payment Link</option>
                  {itineraries.map(it => <option key={it.id} value={it.id}>{it.title} - {it.client_name || 'No Client'} ({fmtMoney(it.total_amount)})</option>)}
                </select>
                <p className="text-xs text-[var(--muted-foreground)] mt-1">When linked, payment will auto-record as a client payment on the itinerary.</p>
              </div>
              <div>
                <label className={labelCls}>{isEdit ? 'Extend Expiration' : 'Link Expires In'}</label>
                <select className={inputCls} value={form.expires_in_hours} onChange={e => set('expires_in_hours', parseInt(e.target.value))}>
                  <option value={24}>{isEdit ? '+24 Hours' : '24 Hours'}</option>
                  <option value={72}>{isEdit ? '+3 Days' : '3 Days'}</option>
                  <option value={168}>{isEdit ? '+7 Days' : '7 Days'}</option>
                  <option value={336}>{isEdit ? '+14 Days' : '14 Days'}</option>
                  <option value={720}>{isEdit ? '+30 Days' : '30 Days'}</option>
                </select>
              </div>
              {!isEdit && (
                <label className="flex items-center gap-2 text-sm text-[var(--foreground)]">
                  <input type="checkbox" checked={form.send_email} onChange={e => set('send_email', e.target.checked)} />
                  Payment Link will be emailed to Client upon creation
                </label>
              )}
            </>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
          <button
            onClick={() => valid && onSave({ ...form, amount: parseFloat(form.amount), expires_in_hours: parseInt(form.expires_in_hours) })}
            disabled={!valid || saving}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 transition-all"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            {saving ? 'Saving…' : isCompleted ? 'Save Changes' : isEdit ? 'Update Payment Link' : 'Create Payment Link'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail drawer ──────────────────────────────────────────────────────────

function LinkDetailDrawer({ link, onClose, onCopy, onResend, onRemind, onEdit, onCancel, onViewPayment }) {
  const typeCfg = TYPE_CFG[link.payment_type] || TYPE_CFG.custom;
  const statusCfg = STATUS_CFG[link.status] || STATUS_CFG.pending;
  const expired = isExpired(link.expires_at);
  const actionable = link.status === 'pending' && !expired;

  return (
    <div className="flex flex-col h-full bg-white border-l border-[var(--border)] overflow-hidden w-full sm:w-[400px] flex-shrink-0">
      <div className="flex-shrink-0 p-5 border-b border-[var(--border)]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] leading-tight truncate">{link.client_name}</div>
            <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{link.client_email}</div>
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0 p-1.5 rounded-lg hover:bg-[var(--muted)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
            {expired && link.status === 'pending' ? 'Expired' : statusCfg.label}
          </span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${typeCfg.bg} ${typeCfg.color}`}>{typeCfg.label}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Payment Details</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Amount</span><span className="font-bold text-[var(--foreground)]">{fmtMoney(link.amount)}</span></div>
            {link.description && <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Description</span><span className="font-medium text-[var(--foreground)] text-right">{link.description}</span></div>}
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Created</span><span className="font-medium text-[var(--foreground)]">{fmtDate(link.created_at)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Travel End</span><span className="font-medium text-[var(--foreground)]">{fmtDate(link.travel_end_date)}</span></div>
            {link.status === 'completed' ? (
              <>
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Completed</span><span className="font-medium text-[var(--foreground)]">{fmtDate(link.completed_at)}</span></div>
                {link.confirmation_number && <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Confirmation</span><span className="font-mono text-xs text-[var(--foreground)]">{link.confirmation_number}</span></div>}
              </>
            ) : (
              <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Expires</span><span className={`font-medium ${expired ? 'text-red-600' : 'text-[var(--foreground)]'}`}>{fmtDate(link.expires_at)}</span></div>
            )}
          </div>
        </div>
        {link.agent_notes && (
          <div>
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Agent Notes</h3>
            <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm text-[var(--foreground)] whitespace-pre-wrap">{link.agent_notes}</div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 p-4 border-t border-[var(--border)] space-y-2">
        {actionable && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => onCopy(link.session_token)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">📋 Copy Link</button>
              <button onClick={() => onResend(link.id)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">📧 Resend</button>
              <button onClick={() => onRemind(link.id)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">🔔 Remind</button>
              <button onClick={() => onEdit(link)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">✏️ Edit</button>
            </div>
            <button onClick={() => onCancel(link.id)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors">Cancel Payment Link</button>
          </>
        )}
        {link.status === 'completed' && (
          <>
            <button onClick={() => onViewPayment(link.id)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--accent)' }}>💳 View Payment Details</button>
            <button onClick={() => onEdit(link)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">📝 Edit Notes</button>
          </>
        )}
        {(link.status === 'expired' || (link.status === 'pending' && expired)) && (
          <div className="text-center text-sm text-[var(--muted-foreground)] py-2">This payment link has expired.</div>
        )}
        {link.status === 'cancelled' && (
          <div className="text-center text-sm text-[var(--muted-foreground)] py-2">This payment link was cancelled.</div>
        )}
      </div>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

const PaymentLinksList = () => {
  const { user } = useAuth();
  const isIndependent = user?.role === 'independent_agent';

  const [paymentLinks, setPaymentLinks] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);

  const [selectedLink, setSelectedLink] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showSecureModal, setShowSecureModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    fetchPaymentLinks();
    fetchItineraries();

    const urlParams = new URLSearchParams(window.location.search);
    const clientParam = urlParams.get('client');
    if (clientParam) {
      try {
        const clientData = JSON.parse(decodeURIComponent(clientParam));
        setEditingLink(null);
        setPrefillData(clientData);
        setShowFormModal(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        console.error('Error parsing client data from URL:', err);
      }
    }
    // eslint-disable-next-line
  }, []);

  const fetchPaymentLinks = async () => {
    try {
      setLoading(true);
      const data = await paymentLinksService.getAll();
      setPaymentLinks(data);
      setError('');
    } catch (err) {
      console.error('Error fetching payment links:', err);
      setError('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  const fetchItineraries = async () => {
    try {
      const data = await itinerariesService.getAll();
      setItineraries(Array.isArray(data) ? data.filter(i => i.status !== 'completed' && i.status !== 'cancelled') : []);
    } catch (err) {
      console.error('Error fetching itineraries:', err);
    }
  };

  const filteredLinks = paymentLinks.filter(link => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      link.client_name?.toLowerCase().includes(term) ||
      link.client_email?.toLowerCase().includes(term) ||
      link.description?.toLowerCase().includes(term) ||
      link.amount?.toString().includes(term);
    const matchesStatus = statusFilter === 'all' || link.status === statusFilter;
    const matchesType = paymentTypeFilter === 'all' || link.payment_type === paymentTypeFilter;
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const linkDate = new Date(link.created_at);
      const now = new Date();
      if (dateFilter === 'today') matchesDate = linkDate.toDateString() === now.toDateString();
      else if (dateFilter === 'week') matchesDate = linkDate >= new Date(now.getTime() - 7 * 86400000);
      else if (dateFilter === 'month') matchesDate = linkDate >= new Date(now.getTime() - 30 * 86400000);
      else if (dateFilter === 'expired') matchesDate = link.status === 'pending' && isExpired(link.expires_at);
    }
    return matchesSearch && matchesStatus && matchesType && matchesDate;
  });

  const stats = {
    total: filteredLinks.length,
    pending: filteredLinks.filter(l => l.status === 'pending').length,
    completed: filteredLinks.filter(l => l.status === 'completed').length,
    totalAmount: filteredLinks.filter(l => l.status === 'completed').reduce((sum, l) => sum + parseFloat(l.amount || 0), 0),
  };

  // ── Actions ────────────────────────────────────────────────────────────
  const copyPaymentLink = (token) => {
    const url = `${window.location.origin}/payment-link/${token}`;
    navigator.clipboard.writeText(url).then(() => alert('Payment link copied to clipboard!')).catch(() => alert('Failed to copy link'));
  };

  const handleResend = async (id) => {
    if (!window.confirm('Are you sure you want to resend this payment link?')) return;
    try {
      await paymentLinksService.resend(id);
      alert('Payment link email resent successfully!');
    } catch (err) {
      console.error('Error resending payment link:', err);
      alert('Failed to resend payment link email');
    }
  };

  const handleRemind = async (id) => {
    try {
      await paymentLinksService.sendReminder(id);
      alert('Payment reminder sent successfully!');
    } catch (err) {
      console.error('Error sending payment reminder:', err);
      alert(`Failed to send reminder: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this payment link?')) return;
    try {
      await paymentLinksService.cancel(id);
      await fetchPaymentLinks();
      setSelectedLink(null);
      alert('Payment link cancelled successfully!');
    } catch (err) {
      console.error('Error cancelling payment link:', err);
      alert('Failed to cancel payment link');
    }
  };

  const openCreateModal = () => { setEditingLink(null); setPrefillData(null); setShowFormModal(true); };
  const openEditModal = (link) => { setEditingLink(link); setPrefillData(null); setShowFormModal(true); };

  const handleSaveForm = async (formData) => {
    try {
      setSaving(true);
      if (editingLink) {
        await paymentLinksService.update(editingLink.id, formData);
        alert('Payment link updated successfully!');
      } else {
        await paymentLinksService.create(formData);
        alert('Payment link created successfully!');
      }
      setShowFormModal(false);
      setEditingLink(null);
      setPrefillData(null);
      setSelectedLink(null);
      await fetchPaymentLinks();
    } catch (err) {
      console.error('Error saving payment link:', err);
      alert(`Failed to save payment link: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleViewPaymentDetails = (paymentLinkId) => {
    const link = paymentLinks.find(l => l.id === paymentLinkId);
    if (!link || !link.payment_id) { alert('Payment details not available for this link'); return; }
    setSelectedPayment({
      payment_ids: [link.payment_id],
      payment_count: 1,
      quote_title: link.description || 'Payment Link Transaction',
      client_name: link.client_name,
      amount: link.amount,
      paymentLinkId,
      paymentLinkStatus: link.status,
    });
    setShowSecureModal(true);
  };

  const toggleSelectId = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleBulkResend = async () => {
    if (!window.confirm(`Are you sure you want to resend ${selectedIds.length} payment links?`)) return;
    try {
      await Promise.all(selectedIds.map(id => paymentLinksService.resend(id)));
      setSelectedIds([]);
      alert('Selected payment links resent successfully!');
    } catch (err) {
      console.error('Error resending payment links:', err);
      alert('Failed to resend some payment links');
    }
  };

  const handleBulkCancel = async () => {
    if (!window.confirm(`Are you sure you want to cancel ${selectedIds.length} payment links?`)) return;
    try {
      await Promise.all(selectedIds.map(id => paymentLinksService.cancel(id)));
      await fetchPaymentLinks();
      setSelectedIds([]);
      alert('Selected payment links cancelled successfully!');
    } catch (err) {
      console.error('Error cancelling payment links:', err);
      alert('Failed to cancel some payment links');
    }
  };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading payment links…</div>;
  }

  return (
    <div className="voyager-app flex h-full min-h-0">
      <div className={`flex-1 overflow-y-auto min-w-0 ${selectedLink ? 'hidden lg:block' : ''}`}>
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Payment Links</h1>
              <p className="text-[var(--muted-foreground)] text-sm">Create and manage secure payment collection links for your clients.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!isIndependent && (
                <button onClick={() => { window.location.href = '/agent/payment-reports'; }} className="px-4 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors" title="View submitted payment information and analytics">
                  📊 Payment Reports
                </button>
              )}
              <button
                onClick={() => { setBulkMode(!bulkMode); setSelectedIds([]); }}
                className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${bulkMode ? 'bg-[var(--muted)] border-[var(--accent)] text-[var(--foreground)]' : 'bg-white border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/50'}`}
              >
                {bulkMode ? 'Cancel Select' : 'Select'}
              </button>
              <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                Create Payment Link
              </button>
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

          {bulkMode && selectedIds.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-[var(--muted)]/60 rounded-xl">
              <span className="text-sm font-semibold text-[var(--foreground)]">{selectedIds.length} selected</span>
              <button onClick={handleBulkResend} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] hover:bg-[var(--muted)] transition-colors">📧 Resend Selected</button>
              <button onClick={handleBulkCancel} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">❌ Cancel Selected</button>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Links', value: stats.total, icon: '🔗' },
              { label: 'Pending', value: stats.pending, icon: '⏳' },
              { label: 'Completed', value: stats.completed, icon: '✅' },
              { label: 'Total Collected', value: fmtMoney(stats.totalAmount), icon: '💰' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-[var(--border)] rounded-xl p-4">
                <div className="text-xl mb-1">{s.icon}</div>
                <div className="text-2xl font-bold text-[var(--primary)]">{s.value}</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                type="text"
                placeholder="Search by client, email, description, or amount…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
              />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer flex-shrink-0">
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_CFG).map(([v, cfg]) => <option key={v} value={v}>{cfg.label}</option>)}
            </select>
            <select value={paymentTypeFilter} onChange={e => setPaymentTypeFilter(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer flex-shrink-0">
              <option value="all">All Types</option>
              {Object.entries(TYPE_CFG).map(([v, cfg]) => <option key={v} value={v}>{cfg.label}</option>)}
            </select>
            <select value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer flex-shrink-0">
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {filteredLinks.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
              <div className="text-4xl mb-3">🔗</div>
              <div className="text-sm font-medium text-[var(--foreground)] mb-1">No payment links found</div>
              <div className="text-xs text-[var(--muted-foreground)]">Create a payment link to start collecting from clients.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredLinks.map(link => {
                const typeCfg = TYPE_CFG[link.payment_type] || TYPE_CFG.custom;
                const statusCfg = STATUS_CFG[link.status] || STATUS_CFG.pending;
                const expired = isExpired(link.expires_at);
                return (
                  <div
                    key={link.id}
                    onClick={() => bulkMode ? toggleSelectId(link.id) : setSelectedLink(link)}
                    className={`w-full text-left bg-white border rounded-xl p-4 cursor-pointer hover:border-[var(--accent)] transition-colors flex items-center gap-4 ${selectedIds.includes(link.id) ? 'border-[var(--accent)] ring-1 ring-[var(--accent)]' : 'border-[var(--border)]'}`}
                  >
                    {bulkMode && <input type="checkbox" checked={selectedIds.includes(link.id)} onChange={() => toggleSelectId(link.id)} onClick={e => e.stopPropagation()} className="flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate text-[var(--foreground)]">{link.client_name}</span>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                          {expired && link.status === 'pending' ? 'Expired' : statusCfg.label}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${typeCfg.bg} ${typeCfg.color}`}>{typeCfg.label}</span>
                      </div>
                      <div className="text-xs text-[var(--muted-foreground)] mt-1 truncate">{link.client_email}{link.description ? ` · ${link.description}` : ''}</div>
                    </div>
                    <div className="text-sm font-bold text-[var(--foreground)] flex-shrink-0">{fmtMoney(link.amount)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedLink && !bulkMode && (
        <LinkDetailDrawer
          link={selectedLink}
          onClose={() => setSelectedLink(null)}
          onCopy={copyPaymentLink}
          onResend={handleResend}
          onRemind={handleRemind}
          onEdit={openEditModal}
          onCancel={handleCancel}
          onViewPayment={handleViewPaymentDetails}
        />
      )}

      {showFormModal && (
        <LinkFormModal
          initial={editingLink}
          prefill={prefillData}
          itineraries={itineraries}
          saving={saving}
          onSave={handleSaveForm}
          onClose={() => { setShowFormModal(false); setEditingLink(null); setPrefillData(null); }}
        />
      )}

      {showSecureModal && selectedPayment && (
        <SecurePaymentDetailsModal
          isOpen={showSecureModal}
          quote={selectedPayment}
          onClose={() => { setShowSecureModal(false); setSelectedPayment(null); }}
          onPaymentDeleted={fetchPaymentLinks}
          isPaymentLink={true}
          paymentLinkId={selectedPayment.paymentLinkId}
        />
      )}
    </div>
  );
};

export default PaymentLinksList;
